import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import type { AdminVoicePhoneNumber } from "../../api/admin";
import { useReleaseAdminVoicePhoneNumber } from "../../hooks/useVoicePhoneNumbers";

interface VoiceNumberReleaseDialogProps {
  number: AdminVoicePhoneNumber | null;
  onClose: () => void;
  onReleased: () => void;
}

export default function VoiceNumberReleaseDialog({
  number,
  onClose,
  onReleased,
}: VoiceNumberReleaseDialogProps) {
  const [reason, setReason] = useState("");
  const releaseMutation = useReleaseAdminVoicePhoneNumber();

  useEffect(() => {
    if (number) setReason("");
  }, [number]);

  const submit = async () => {
    if (!number || !reason.trim()) return;
    const result = await releaseMutation.mutateAsync({
      voicePhoneNumberId: number.voice_phone_number_id,
      reason: reason.trim(),
      cooldownDays: 30,
    });
    if (result.ok) {
      onReleased();
      onClose();
    }
  };

  return (
    <Dialog open={Boolean(number)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>Release number</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography sx={{ fontSize: "0.86rem", color: "#475569" }}>
          Releasing {number?.phone_number} removes the business assignment and puts the number into a 30-day cooldown.
        </Typography>
        <TextField
          label="Release reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          multiline
          minRows={3}
          size="small"
          required
          fullWidth
        />
        {releaseMutation.isError && (
          <Alert severity="error">Failed to release this number.</Alert>
        )}
        {releaseMutation.data?.ok === false && (
          <Alert severity="error">{releaseMutation.data.message ?? "Release was blocked."}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!reason.trim() || releaseMutation.isPending}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Release number
        </Button>
      </DialogActions>
    </Dialog>
  );
}
